/**
 * Presence classification helpers for the Live monitor.
 *
 * "Online now" is TIME-BASED and must be re-evaluated on a ticker (see useNow),
 * not only on snapshot changes — otherwise a stale doc appears online forever.
 */
import { PRESENCE_ONLINE_WINDOW_MS, type Presence } from '@mitang/shared';

/** Window after going offline during which a user still counts as "recently active". */
export const RECENTLY_ACTIVE_WINDOW_MS = 10 * 60 * 1000; // 10 minutes

/**
 * A user is online iff their session is not explicitly offline AND their last
 * heartbeat is within the online window relative to `now`.
 */
export function isOnline(p: Presence, now: number): boolean {
  return p.state !== 'offline' && now - p.lastSeen < PRESENCE_ONLINE_WINDOW_MS;
}

/**
 * "Recently active" = not online, but seen within the recent window. Covers
 * backgrounded sessions and users who just left.
 */
export function isRecentlyActive(p: Presence, now: number): boolean {
  return !isOnline(p, now) && now - p.lastSeen < RECENTLY_ACTIVE_WINDOW_MS;
}

/**
 * Human-readable "what they're doing". Prefers the app-provided label, then
 * derives a sensible fallback from the machine `currentView` key
 * ('home' | 'category:LAPTOPS' | 'product:<id>' | 'search'), then a generic one.
 */
export function activityLabel(p: Presence): string {
  const label = p.currentLabel?.trim();
  if (label) return label;

  const view = p.currentView?.trim();
  if (view) {
    if (view === 'home') return 'On the home screen';
    if (view === 'search') return 'Searching';
    const [kind, ...restParts] = view.split(':');
    const rest = restParts.join(':').trim();
    if (kind === 'category' && rest) return `Browsing ${rest}`;
    if (kind === 'product') return 'Viewing a product';
    return view;
  }

  return 'In the app';
}

/** Short display name for a dealer: name, with company/city as a subtitle line. */
export function subtitle(p: Presence): string {
  const parts = [p.companyName?.trim(), p.city?.trim()].filter(
    (v): v is string => Boolean(v),
  );
  return parts.join(' · ');
}
